// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import assertNever from "assert-never";
import {
  add,
  differenceInDays,
  differenceInMonths,
  endOfToday,
  getMonth,
  getYear,
  isAfter,
  parseISO,
  startOfToday,
} from "date-fns";
import { Timestamp } from "firebase/firestore";
import { groupBy, mapValues, sortBy } from "lodash";
import moment from "moment";

import {
  fieldToDate,
  OpportunityType,
  StaffRecord,
  SystemId,
} from "~datatypes";
import { FilterOption } from "~shared-pathways";

import {
  INSIGHTS_PATHS,
  insightsUrl,
  WORKFLOWS_SYSTEM_ID_TO_PAGE,
  WorkflowsPathSection,
  workflowsUrl,
} from "../core/views";
import {
  AutoSnoozeUpdate,
  CombinedUserRecord,
  ManualSnoozeUpdate,
} from "../FirestoreStore/types";
import { ActiveFeatureVariantRecord } from "../RootStore/types";
import {
  Opportunity,
  OpportunityTab,
  OpportunityTabGroup,
} from "./Opportunity";
import { OpportunityConfiguration } from "./Opportunity/OpportunityConfigurations";
import { JusticeInvolvedPerson, StaffFilterFunction } from "./types";

/**
 * Returns a string of the month and year formatted as "MM_YYYY"
 */
export function getMonthYearFromDate(date: Date): string {
  const month = getMonth(date) + 1;
  return month < 10
    ? `0${month}_${getYear(date)}`
    : `${month}_${getYear(date)}`;
}

export function formatSupervisionType(supervisionType: string): string {
  return supervisionType.replace("_", " ");
}

export function dateToTimestamp(isodate: string): Timestamp {
  return new Timestamp(new Date(isodate).getTime() / 1000, 0);
}

export function validatePhoneNumber(phoneNumber: string | undefined): boolean {
  if (!phoneNumber) return false;
  const phoneNumberRegex = /^\d{10}$/;
  return phoneNumberRegex.test(clearPhoneNumberFormatting(phoneNumber));
}

export function clearPhoneNumberFormatting(phoneNumber: string) {
  return phoneNumber.replace(/\D/g, "");
}

export function formatPhoneNumber(phoneNumber: string): string {
  const digitsOnly = clearPhoneNumberFormatting(phoneNumber);
  const formattedPhoneNumber = digitsOnly.replace(
    /(\d{3})(\d{3})(\d{4})/,
    "($1) $2-$3",
  );

  return formattedPhoneNumber;
}

export function formatFacilityHousingUnit(
  facilityId: string | undefined,
  unitId: string | undefined,
): string {
  const formattedFacilityHousingUnit = `${facilityId ?? ""}${
    facilityId && unitId ? "/" : ""
  }${unitId ?? ""}`;
  return formattedFacilityHousingUnit;
}

export function optionalFieldToDate(
  field?: Timestamp | string | null,
): Date | undefined {
  if (field) return fieldToDate(field);
}

export function fieldToDateArray(field: Timestamp[] | string[]): Date[] {
  return field.map((d: Timestamp | string) => fieldToDate(d));
}

export function optionalFieldToDateArray(
  field?: Timestamp[] | string[],
): Date[] | undefined {
  if (field) return fieldToDateArray(field);
}

export function fractionalDateBetweenTwoDates(
  dateLeft: Date | undefined,
  dateRight: Date | undefined,
  fractionalPortion: number,
): Date | undefined {
  if (dateLeft && dateRight) {
    const diffInDays = moment(dateRight).diff(moment(dateLeft), "days");
    const fractionOfDays = Math.floor(diffInDays * fractionalPortion);
    const fractionalDate = moment(dateLeft)
      .add(fractionOfDays, "days")
      .toDate();
    return fractionalDate;
  }
}

export function staffNameComparator(a: StaffRecord, b: StaffRecord): number {
  const alphabetAndSpacesOnlyRegex = /[^a-zA-Z\s]+/g;

  const surnameA = String(a.surname)
    .toLowerCase()
    .replace(alphabetAndSpacesOnlyRegex, "");
  const givenNamesA = String(a.givenNames)
    .toLowerCase()
    .replace(alphabetAndSpacesOnlyRegex, "");

  const surnameB = String(b.surname)
    .toLowerCase()
    .replace(alphabetAndSpacesOnlyRegex, "");
  const givenNamesB = String(b.givenNames)
    .toLowerCase()
    .replace(alphabetAndSpacesOnlyRegex, "");

  if (surnameA > surnameB) return 1;
  if (surnameA < surnameB) return -1;
  if (surnameA === surnameB) {
    return String(givenNamesA).localeCompare(givenNamesB);
  }
  return 0;
}

// Note that denial reason of type 'Other' will create an 'other reasons' denial text box,
// regardless of the case (ex: 'OTHER', 'other', 'Other', etc) of the denial reason
export const OTHER_KEY = "Other";

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];
export const getEntries = <T extends object>(obj: T) =>
  Object.entries(obj) as Entries<T>;

export function getJusticeInvolvedPersonTitle(
  systemId: SystemId | undefined,
): string {
  switch (systemId) {
    case "INCARCERATION":
      return "resident";
    case "SUPERVISION":
      return "client";
    case "ALL":
    case undefined:
      return "person";
    default:
      assertNever(systemId);
  }
}

export function getSystemIdFromPage(
  page: WorkflowsPathSection,
): SystemId | undefined {
  if (WORKFLOWS_SYSTEM_ID_TO_PAGE.INCARCERATION.includes(page)) {
    return "INCARCERATION";
  }
  if (WORKFLOWS_SYSTEM_ID_TO_PAGE.SUPERVISION.includes(page)) {
    return "SUPERVISION";
  }
  if (WORKFLOWS_SYSTEM_ID_TO_PAGE.ALL.includes(page)) {
    return "ALL";
  }
}

export const usMiFilterByUserDistrict: StaffFilterFunction = (
  user: CombinedUserRecord,
  featureVariants: ActiveFeatureVariantRecord,
) => {
  const staffFilter = filterByUserDistrict(user, featureVariants);

  if (
    !featureVariants.supervisionUnrestrictedSearch &&
    !user.updates?.overrideDistrictIds
  ) {
    if (user.info.stateCode === "US_MI" && user.info.district?.includes("10")) {
      return {
        filterField: "district",
        filterValues: [
          "10 - WEST",
          "10 - CENTRAL",
          "10 - NORTHEAST",
          "10 - NORTHWEST",
        ],
      };
    }
  }

  return staffFilter;
};

export const filterByUserDistrict: StaffFilterFunction = (
  user: CombinedUserRecord,
  featureVariants: ActiveFeatureVariantRecord,
) => {
  if (featureVariants.supervisionUnrestrictedSearch) return;

  let filterValues = user.updates?.overrideDistrictIds;

  if (!filterValues?.length) {
    if (user.info.district) {
      filterValues = [user.info.district];
    } else {
      // If the user doesn't have a district, only let them search for themselves
      return {
        filterField: "email",
        filterValues: [user.info.email],
      };
    }
  }

  return {
    filterField: "district",
    filterValues,
  };
};

// TODO(OBT-49983): Remove once the Typesense-backed search refactor ships.
//
// Shared value for `WorkflowsSystemConfig.maxFacilityUnitSearchIds`, added
// as a temporary mitigation for OBT-48882: a handful of large facility
// units is enough to pull hundreds of residents into a single unbounded,
// unpaginated Firestore listener, exhausting Firestore's request quota and
// crashing the app for that user.
export const DEFAULT_MAX_FACILITY_UNIT_SEARCH_IDS = 4;

export const usCaFilterByRoleSubtype: StaffFilterFunction = (
  user: CombinedUserRecord,
  featureVariants: ActiveFeatureVariantRecord,
) => {
  // Allow any Parole Agent supervisors to access any caseload
  if (
    featureVariants.supervisionUnrestrictedSearch ||
    user.info.roleSubtype === "SUPERVISION_OFFICER_SUPERVISOR"
  )
    return;

  // TODO(#4618): Consider getting rid of the role subtype comparison and replacing the relevant
  // logic with a feature variant

  // Regular Parole Agents only get access to their own caseload If we don't have a district for a
  // user and we didn't list them as leadership, restrict them to their own caseload as well.
  if (
    user.info.roleSubtype === "SUPERVISION_OFFICER" ||
    (!user.info.district && !user.updates?.overrideDistrictIds)
  ) {
    return {
      // Use email instead of id because users might not have a staff record or an id set in the
      // admin panel
      filterField: "email",
      filterValues: [user.info.email],
    };
  }

  // Parole Agent supervisors only get access to their own unit (district in our schema)
  return filterByUserDistrict(user, featureVariants);
};

/* Returns the snooze until date from either the auto or manual snooze updates. */
export function getSnoozeUntilDate({
  snoozeUntil,
  snoozeForDays,
  snoozedOn,
}: Partial<AutoSnoozeUpdate | ManualSnoozeUpdate>): Date | undefined {
  if (snoozeUntil) return parseISO(snoozeUntil);
  if (snoozedOn && snoozeForDays)
    return add(parseISO(snoozedOn), { days: snoozeForDays });
}

export function snoozeUntilDateInTheFuture(snoozeUntilDate: Date) {
  return snoozeUntilDate > endOfToday();
}

/**
 * Returns true if the opportunity is eligible and not submitted and not denied.
 * This helper function is used to count/filter for eligible opportunities
 * particularly on the Supervisor Homepage.
 */
export function isEligible(opp: Opportunity) {
  return !opp.almostEligible && !opp.isSubmitted && !opp.denied;
}

/**
 * Organizes opportunities by both opportunity type and the relevant tab grouping.
 */
export function opportunitiesByTab(
  allOpportunitiesByType: Partial<Record<OpportunityType, Opportunity[]>>,
  tabGroup?: OpportunityTabGroup,
  reviewerId?: string,
  // TODO(OBT-53651): Remove once reviewer fields are migrated to stable IDs.
  legacyReviewerId?: string,
): Partial<Record<OpportunityType, Record<OpportunityTab, Opportunity[]>>> {
  return mapValues(allOpportunitiesByType, (opps) => {
    return opportunitiesByTabForType(
      opps,
      tabGroup,
      reviewerId,
      legacyReviewerId,
    );
  });
}

export function opportunitiesByTabForType(
  opportunities?: Opportunity<JusticeInvolvedPerson>[] | undefined,
  tabGroup?: OpportunityTabGroup,
  reviewerId?: string,
  // TODO(OBT-53651): Remove once reviewer fields are migrated to stable IDs.
  legacyReviewerId?: string,
): Record<OpportunityTab, Opportunity[]> {
  return groupBy(opportunities, (opp) =>
    opp.tabTitle(
      (tabGroup || Object.keys(opp.config.tabGroups)[0]) as any,
      reviewerId,
      legacyReviewerId,
    ),
  ) as Record<OpportunityTab, Opportunity[]>;
}

/**
 * Return a map from subcategory to a list of opportunities from that subcategory
 * from the given list, or undefined if none of the opportunities have a subcategory.
 * Assumes that either all opportunities in the list have a subcategory or none do.
 */
export function opportunitiesBySubcategory(
  opportunities: Opportunity[],
): Record<string, Opportunity[]> | undefined {
  if (!opportunities.length || !opportunities[0].subcategory) {
    return;
  }
  return groupBy(opportunities, (opp) => opp.subcategory);
}

/**
 * Return the relevant url for the "navigate to form" button.
 */
export function getLinkToForm(
  pathname: string,
  opportunity: Opportunity,
  officerPseudoId: string | undefined,
  supervisorPseudoId: string | undefined,
): string {
  const isInsights = pathname.startsWith(INSIGHTS_PATHS.supervision);
  const { urlSection } = opportunity.config;
  const justiceInvolvedPersonId = opportunity.person.pseudonymizedId;
  const opportunityPseudoId = opportunity.selectId;
  const useInsightsOfficerUrl = isInsights && officerPseudoId;
  const useInsightsSupervisorUrl = isInsights && supervisorPseudoId;
  if (useInsightsOfficerUrl)
    return insightsUrl("supervisionOpportunityForm", {
      officerPseudoId,
      opportunityTypeUrl: urlSection,
      clientPseudoId: justiceInvolvedPersonId,
      opportunityPseudoId,
    });
  if (useInsightsSupervisorUrl)
    return insightsUrl("supervisionSupervisorOpportunityForm", {
      supervisorPseudoId,
      opportunityTypeUrl: urlSection,
      clientPseudoId: justiceInvolvedPersonId,
      opportunityPseudoId,
    });
  return workflowsUrl("opportunityAction", {
    urlSection,
    justiceInvolvedPersonId,
    opportunityPseudoId,
  });
}
export { fieldToDate };

/**
 * Supervision level options by tenant for Workflows.
 * These were previously sourced from the pathways filterOptions,
 * but the dashboard-only tenants (US_PA, US_TX, US_IA) were removed from that
 * map when it moved to shared-pathways. Pathways is moving to dynamic filter options
 * coming in the metrics response metadata so the static map will eventually be removed.
 * Workflows still needs them, so they are preserved here.
 */
export const workflowsSupervisionLevelOptionsByTenant: Record<
  string,
  FilterOption[]
> = {
  US_ID: [
    { label: "All", value: "ALL" },
    { label: "Low", value: "MINIMUM" },
    { label: "Moderate", value: "MEDIUM" },
    { label: "High", value: "HIGH" },
    { label: "Diversion", value: "DIVERSION" },
    { label: "Interstate", value: "INTERSTATE_COMPACT" },
    { label: "In custody", value: "IN_CUSTODY" },
    { label: "Unassigned", value: "UNASSIGNED" },
    { label: "Unsupervised", value: "UNSUPERVISED" },
    { label: "Limited supervision", value: "LIMITED" },
    { label: "Other", value: "OTHER" },
    { label: "Drug Court", value: "DRUG COURT" },
    { label: "Mental Health Court", value: "MENTAL HEALTH COURT" },
    { label: "Absconsion", value: "ABSCONSION" },
    { label: "Maximum", value: "MAXIMUM" },
    { label: "Bench Warrant", value: "BENCH WARRANT" },
    { label: "Veterans Court", value: "VETERANS COURT" },
    { label: "Family Court", value: "FAMILY COURT" },
    { label: "Administrative", value: "ADMINISTRATIVE" },
    { label: "Probation Violator", value: "PROBATION VIOLATOR" },
    { label: "Dosage", value: "DOSAGE" },
    { label: "DUI Court", value: "DUI COURT" },
    { label: "Drug Court Div.", value: "DRUG COURT DIV" },
    { label: "Unknown", value: "INTERNAL_UNKNOWN" },
  ],
  US_MO: [
    { label: "All", value: "ALL" },
    { label: "Incarcerated", value: "INCARCERATED" },
    { label: "Low", value: "MINIMUM" },
    { label: "Moderate", value: "MEDIUM" },
    { label: "High", value: "HIGH" },
    { label: "Maximum", value: "MAXIMUM" },
    { label: "Electronic Monitoring", value: "ELECTRONIC_MONITORING_ONLY" },
    { label: "Unknown", value: "UNKNOWN" },
    { label: "Unassigned", value: "UNASSIGNED" },
    { label: "Other", value: "OTHER" },
  ],
  US_ND: [
    { label: "All", value: "ALL" },
    { label: "Limited", value: "LIMITED" },
    { label: "Minimum", value: "MINIMUM" },
    { label: "Medium", value: "MEDIUM" },
    { label: "Maximum", value: "MAXIMUM" },
    { label: "High", value: "HIGH" },
  ],
  US_TN: [
    { label: "All", value: "ALL" },
    { label: "Absconded", value: "ABSCONSION" },
    { label: "Furlough", value: "FURLOUGH" },
    { label: "Minimum", value: "MINIMUM" },
    { label: "Medium", value: "MEDIUM" },
    { label: "Maximum", value: "HIGH" },
    { label: "Enhanced", value: "MAXIMUM" },
    { label: "In Custody", value: "IN_CUSTODY" },
    { label: "Compliant Reporting", value: "LIMITED" },
    { label: "Inactive", value: "UNSUPERVISED" },
    { label: "Intake", value: "UNASSIGNED" },
    { label: "Warrant", value: "WARRANT" },
    { label: "ICOTS", value: "INTERSTATE_COMPACT" },
    { label: "Other", value: "OTHER" },
    { label: "Primary", value: "6P1" },
    { label: "Secondary", value: "6P2" },
    { label: "Intermediate", value: "6P3" },
    { label: "Transitional", value: "6P4" },
    { label: "Low", value: "8LO" },
    { label: "Moderate", value: "8MO" },
    { label: "Unknown", value: "UNKNOWN" },
    { label: "8LC", value: "8LC" },
    { label: "Residential Program", value: "RESIDENTIAL_PROGRAM" },
  ],
  US_PA: [
    { label: "All", value: "ALL" },
    { label: "Minimum", value: "MINIMUM" },
    { label: "Medium", value: "MEDIUM" },
    { label: "Maximum", value: "MAXIMUM" },
    { label: "Administrative", value: "ADM" },
    { label: "Special Circumstances", value: "SPC" },
    { label: "Unknown", value: "UNKNOWN" },
  ],
  US_TX: [
    { label: "All", value: "ALL" },
    { label: "High", value: "MAXIMUM" },
    { label: "Moderate", value: "HIGH" },
    { label: "Low-Moderate", value: "MEDIUM" },
    { label: "Low", value: "MINIMUM" },
    { label: "Annual", value: "LIMITED" },
    { label: "In-custody", value: "IN_CUSTODY" },
    { label: "Residential Reentry", value: "RESIDENTIAL_PROGRAM" },
  ],
  US_IA: [
    { label: "Level 0", value: "LEVEL 0" },
    { label: "Level 1", value: "LEVEL 1" },
    { label: "Level 2", value: "LEVEL 2" },
    { label: "Level 3", value: "LEVEL 3" },
    { label: "Level 4", value: "LEVEL 4" },
    { label: "Level 5", value: "LEVEL 5" },
    { label: "Low Risk Probation", value: "LOW RISK PROBATION" },
    { label: "Global Positioning", value: "GLOBAL POSITIONING" },
    { label: "Absconded", value: "ABSCONDED" },
    { label: "Minimum Risk Program", value: "MINIMUM RISK PROGRAM" },
    {
      label: "Detained by non-Iowa Jurisdiction",
      value: "DETAINED BY NON-IOWA JURISDICTION",
    },
    { label: "In Jail", value: "IN JAIL" },
    { label: "Escaped", value: "ESCAPED" },
    { label: "Paroled to Detainer", value: "PAROLED TO DETAINER" },
    { label: "In Prison", value: "IN PRISON" },
    { label: "Day Reporting", value: "DAY REPORTING" },
    { label: "Jail (Designated Site)", value: "JAIL (DESIGNATED SITE)" },
    { label: "Intensive Supervision", value: "INTENSIVE SUPERVISION" },
    {
      label: "Detained by Another State",
      value: "DETAINED BY ANOTHER STATE",
    },
    { label: "Electronic Monitoring", value: "ELECTRONIC MONITORING" },
    { label: "Radio Frequency", value: "RADIO FREQUENCY" },
    {
      label: "Global Positioning Satellite",
      value: "GLOBAL POSITIONING SATELLITE",
    },
    { label: "Day Reporting Supervision", value: "DAY REPORTING SUPERVISION" },
    {
      label: "Concurrent Prison Sentence",
      value: "CONCURRENT PRISON SENTENCE",
    },
    { label: "Warrant", value: "WARRANT" },
    { label: "Diversion Program", value: "DIVERSION PROGRAM" },
  ],
  US_DEMO: [
    { label: "All", value: "ALL" },
    { label: "Limited", value: "LIMITED" },
    { label: "Minimum", value: "MINIMUM" },
    { label: "Medium", value: "MEDIUM" },
    { label: "Maximum", value: "MAXIMUM" },
    { label: "High", value: "HIGH" },
  ],
};

const defaultSupervisionLevelOptions: FilterOption[] = [
  { label: "All", value: "ALL" },
  { label: "Limited", value: "LIMITED" },
  { label: "Minimum", value: "MINIMUM" },
  { label: "Medium", value: "MEDIUM" },
  { label: "Maximum", value: "MAXIMUM" },
  { label: "High", value: "HIGH" },
];

export function getWorkflowsSupervisionLevelOptions(
  tenantId: string | undefined,
): FilterOption[] {
  return (
    workflowsSupervisionLevelOptionsByTenant[tenantId ?? ""] ??
    defaultSupervisionLevelOptions
  );
}

/**
 * Represents difference from current date as text. Durations under two years
 * represented as months, otherwise truncated to full years.
 */
export function formatRelativeToNow(start: Date): string {
  const months = differenceInMonths(new Date(), start);
  if (months < 24) return `${months} months`;
  return `${Math.floor(months / 12)} years`;
}

export const sortObject = (o: object) =>
  Object.fromEntries(sortBy(Object.entries(o), 0));

/**
 * Returns the relevant form url withouth needing an opportunity obj, relevant for ineligible clients
 */
export function getLinkToFormWithoutOpportunity(
  pathname: string,
  officerPseudoId: string | undefined,
  person: JusticeInvolvedPerson,
  config: OpportunityConfiguration,
): string {
  const isInsights = pathname.startsWith(INSIGHTS_PATHS.supervision);
  const { urlSection } = config;
  const justiceInvolvedPersonId = person.pseudonymizedId;
  const linkToForm =
    isInsights && officerPseudoId
      ? insightsUrl("supervisionOpportunityForm", {
          officerPseudoId,
          opportunityTypeUrl: urlSection,
          clientPseudoId: justiceInvolvedPersonId,
          opportunityPseudoId: justiceInvolvedPersonId,
        })
      : workflowsUrl("opportunityAction", {
          urlSection,
          justiceInvolvedPersonId,
          opportunityPseudoId: justiceInvolvedPersonId,
        });
  return linkToForm;
}

/**
 * Returns the release date if it exists for the given person.
 */
export function getPersonReleaseDate(
  person: JusticeInvolvedPerson,
): Date | undefined {
  if ("expirationDate" in person && person.expirationDate instanceof Date) {
    return person.expirationDate;
  } else if ("releaseDate" in person && person.releaseDate instanceof Date) {
    return person.releaseDate;
  }
  return undefined;
}

/**
 * Return the number of days until the given person's release date. If release date
 * is in the past, returns Infinity.
 */
export function getPersonDaysToRelease(person: JusticeInvolvedPerson): number {
  const releaseDate = getPersonReleaseDate(person);
  return releaseDate !== undefined && isAfter(releaseDate, startOfToday())
    ? differenceInDays(releaseDate, startOfToday())
    : Infinity;
}
